import { siteName as configSiteName, isMultiTenant } from "@/Configs/config";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { Site as SelectedSite } from "server/sites/index.js";

type SiteContextType = {
  siteName: string | undefined;
  selectedSite: SelectedSite | undefined;
  setSelectedSite: (site?: SelectedSite) => void;
  namespace: string | undefined;
};

// Context and Provider
const SiteContext = createContext<SiteContextType | undefined>(undefined);

function safeJsonParse(str: string) {
  try {
    return JSON.parse(str);
  } catch {
    return undefined;
  }
}

const initialSite = safeJsonParse(
  localStorage.getItem("selectedSite") ?? "",
) as SelectedSite | undefined;

export const SiteProvider = ({ children }: { children: ReactNode }) => {
  const [selectedSite, _setSelectedSite] = useState(initialSite);

  const setSelectedSite = useCallback((site?: SelectedSite) => {
    localStorage.setItem("selectedSite", JSON.stringify(site));
    _setSelectedSite(site);
  }, []);

  const siteName = useMemo(() => {
    if (isMultiTenant) {
      return selectedSite?.siteName || undefined;
    }
    return configSiteName;
  }, [selectedSite]);

  const namespace = useMemo(() => {
    if (isMultiTenant) {
      return selectedSite?.namespace || undefined;
    }
    return configSiteName;
  }, [selectedSite]);

  const context = useMemo(
    () => ({
      selectedSite,
      siteName,
      setSelectedSite,
      namespace,
    }),
    [selectedSite, setSelectedSite, siteName, namespace],
  );

  return (
    <SiteContext.Provider value={context}>{children}</SiteContext.Provider>
  );
};

/**
 *
 * @returns {SiteContextType}
 * - siteName: string | undefined - the name of the site, either from the selected site or from environment config
 * - selectedSite: Site | undefined - the site selected by the user
 * - setSelectedSite: (site: Site) => void - function to set the selected site
 * - namespace: string | undefined - the namespace of the selected site used for DWH queries, either from the selected site or from environment config
 */
export const useSelectedSite = (): SiteContextType => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error("useSites must be used within an SiteProvider");
  }
  return context;
};
